import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectMenuPermissions } from "../../../app/permissionsSlice";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import {
  FiBell, FiPlus, FiTrash2, FiEdit3, FiCheckCircle, FiInfo,
  FiSearch, FiTag, FiAlertTriangle, FiPackage, FiCreditCard,
  FiAward, FiToggleLeft, FiToggleRight, FiCalendar, FiMessageSquare,
  FiUsers, FiUser, FiZap, FiSend, FiImage, FiStar, FiEye,
  FiChevronUp, FiChevronDown, FiPlusCircle,
} from "react-icons/fi";

// ─── Font style shorthand ───────────────────────────────────────────────────────
const INTER = { fontFamily: "Inter, sans-serif" };

// ─── Category Config ────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: "Offer",   label: "Offer",   icon: FiTag,           color: "#b45309", bg: "#fef3c7", border: "#fde68a" },
  { value: "Alert",   label: "Alert",   icon: FiAlertTriangle, color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  { value: "Arrival", label: "Arrival", icon: FiPackage,        color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  { value: "Payment", label: "Payment", icon: FiCreditCard,    color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
  { value: "Scheme",  label: "Scheme",  icon: FiAward,         color: "#7e22ce", bg: "#faf5ff", border: "#e9d5ff" },
  { value: "General", label: "General", icon: FiBell,          color: "#475569", bg: "#f8fafc", border: "#e2e8f0" },
];

const METAL_TYPES = [
  { value: "Gold",     label: "Gold",     color: "#b78a39", bg: "#fefce8", border: "#fde68a" },
  { value: "Silver",   label: "Silver",   color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" },
  { value: "Diamond",  label: "Diamond",  color: "#0ea5e9", bg: "#f0f9ff", border: "#bae6fd" },
  { value: "Platinum", label: "Platinum", color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe" },
  { value: "Other",    label: "Other",    color: "#475569", bg: "#f1f5f9", border: "#cbd5e1" },
];

function getCategoryStyle(val) {
  return CATEGORIES.find(c => c.value === val) || CATEGORIES[5];
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ─── Empty form templates ───────────────────────────────────────────────────────
const EMPTY_NOTIF_FORM = {
  Auto: 0,
  Category: "General",
  Title: "",
  Message: "",
  ImageUrl: "",
  ActionUrl: "",
  IsActive: true,
  ExpiresAt: "",
  Priority: 0,
  TargetCustomerValue: "",
  TargetCustomerLabel: "",
  TargetLocation: "",
};

const EMPTY_ARRIVAL_FORM = {
  Auto: 0,
  Category: "Arrival",
  Title: "",
  MetalType: "Gold",
  Purity: "22K (916)",
  Weight: "",
  PricingType: "dynamic", // "dynamic" | "fixed"
  FixedPrice: "",
  MakingCharge: "",
  MakingChargeType: "per_gram", // "per_gram" | "percentage" | "flat"
  GstPercent: 3,
  StockStatus: "in_stock", // "in_stock" | "out_of_stock" | "pre_order"
  BookingEnabled: true,
  BookingAmount: "",
  Message: "", // Used as description locally
  IsActive: true,
  ExpiresAt: "",
  Priority: 0,
  TargetCustomerValue: "",
  TargetCustomerLabel: "",
};

const MAX_IMAGES = 8;

const PURITY_OPTIONS = {
  Gold: ["24K (999)", "22K (916)", "18K (750)", "14K (585)"],
  Silver: ["Fine (99.9)", "Sterling (92.5)", "Alloy"],
  Diamond: ["18K (750) White Gold", "18K (750) Yellow Gold", "Platinum PT950"],
  Platinum: ["PT950 (95%)", "PT900 (90%)"],
  Other: ["Standard"],
};

// ─── Multi-Image Manager sub-component ─────────────────────────────────────────
function ImageManager({ images, newUrlInput, onNewUrlChange, onAddImage, onDeleteImage, onMoveUp, onMoveDown, isSaving, inputRef, onKeyDown }) {
  const coverImg = images[0];
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
          <FiImage className="w-3 h-3 text-emerald-500" />
          Product Images
          <span className="text-[9px] text-slate-400 font-normal ml-1">(max {MAX_IMAGES})</span>
        </label>
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
          {images.length}/{MAX_IMAGES}
        </span>
      </div>

      {/* Image grid */}
      {images.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {images.map((img, idx) => (
            <div key={img.Auto || img._tempId}
              className={`flex items-center gap-2 p-1.5 rounded-lg border ${
                idx === 0
                  ? "border-emerald-300 bg-emerald-50/50"
                  : "border-slate-200 bg-slate-50"
              }`}
            >
              {/* Thumbnail */}
              <div className="w-9 h-9 rounded-md border border-slate-200 bg-white overflow-hidden flex-shrink-0 flex items-center justify-center">
                <img src={img.ImageUrl} alt=""
                  className="w-full h-full object-cover"
                  onError={e => { e.target.style.display="none"; }}
                />
              </div>
              {/* URL text */}
              <div className="flex-1 min-w-0">
                {idx === 0 && (
                  <span className="text-[8.5px] font-bold text-emerald-600 block leading-none mb-0.5" style={INTER}>COVER</span>
                )}
                <p className="text-[10px] text-slate-500 truncate" style={INTER}>{img.ImageUrl}</p>
              </div>
              {/* Controls */}
              <div className="flex items-center gap-0.5 shrink-0">
                <button type="button" onClick={() => onMoveUp(idx)} disabled={idx === 0}
                  className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer border-none bg-transparent transition-colors">
                  <FiChevronUp className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => onMoveDown(idx)} disabled={idx === images.length - 1}
                  className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer border-none bg-transparent transition-colors">
                  <FiChevronDown className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => onDeleteImage(idx, img)}
                  className="w-5 h-5 flex items-center justify-center rounded text-slate-400 hover:text-red-500 hover:bg-red-50 cursor-pointer border-none bg-transparent transition-colors">
                  <FiTrash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add URL input */}
      {images.length < MAX_IMAGES && (
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            ref={inputRef}
            value={newUrlInput}
            onChange={e => onNewUrlChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                if (newUrlInput.trim()) {
                  onAddImage();
                } else if (onKeyDown) {
                  onKeyDown(e);
                }
              }
            }}
            placeholder="Paste image URL and press Add..."
            className="flex-1 h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11px] outline-none hover:border-slate-300 focus:border-emerald-600 transition-all"
            style={INTER}
          />
          <button type="button" onClick={onAddImage} disabled={!newUrlInput.trim() || isSaving}
            className="h-[28px] px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-[10.5px] font-bold rounded-md flex items-center gap-1 cursor-pointer border-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
            style={INTER}>
            <FiPlusCircle className="w-3 h-3" /> Add
          </button>
        </div>
      )}

      {/* Cover preview large */}
      {coverImg && (
        <div className="rounded-lg overflow-hidden border border-emerald-200 bg-slate-900 relative" style={{ height: 80 }}>
          <img src={coverImg.ImageUrl} alt="cover"
            className="w-full h-full object-cover opacity-80"
            onError={e => { e.target.style.display="none"; }}
          />
          <span className="absolute bottom-1 left-1.5 text-[8.5px] font-bold text-white bg-emerald-700/80 px-1.5 py-0.5 rounded" style={INTER}>COVER PHOTO</span>
        </div>
      )}
    </div>
  );
}

// ─── Stat Card ──────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-2 bg-slate-50 px-2.5 py-1 rounded-xl border border-slate-200/80 shrink-0">
      <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: color + "22", color }}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className="leading-tight">
        <span className="text-[9.5px] text-slate-400 font-bold block uppercase tracking-wider mb-0.5">{label}</span>
        <span className="text-[14px] font-extrabold text-slate-700">{value}</span>
      </div>
    </div>
  );
}

// ─── Format Google Drive Image URLs to direct sources ─────────────────────────
function formatImageUrl(url) {
  if (!url) return "";
  const trimmed = url.trim();
  // Match drive.google.com/file/d/ID/view
  const fileIdMatch = trimmed.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileIdMatch && fileIdMatch[1]) {
    return `https://lh3.googleusercontent.com/d/${fileIdMatch[1]}`;
  }
  // Match drive.google.com/open?id=ID
  const openIdMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (trimmed.includes("drive.google.com") && openIdMatch && openIdMatch[1]) {
    return `https://lh3.googleusercontent.com/d/${openIdMatch[1]}`;
  }
  return trimmed;
}

// ─── Compose arrival message from metadata ─────────────────────────────────────
function composeArrivalMessage(form) {
  const payload = {
    metalType: form.MetalType,
    purity: form.Purity,
    weight: parseFloat(form.Weight) || 0,
    pricingType: form.PricingType || "dynamic",
    fixedPrice: parseFloat(form.FixedPrice) || 0,
    makingCharge: parseFloat(form.MakingCharge) || 0,
    makingChargeType: form.MakingChargeType || "per_gram",
    gstPercent: parseFloat(form.GstPercent) || 3,
    stockStatus: form.StockStatus || "in_stock",
    bookingEnabled: form.BookingEnabled !== undefined ? form.BookingEnabled : true,
    bookingAmount: parseFloat(form.BookingAmount) || 0,
    description: form.Message.trim(),
  };
  return JSON.stringify(payload);
}

// ─── Parse arrival message back to components ────────────────────────────────────
function parseArrivalMessage(rawMsg) {
  if (!rawMsg) {
    return {
      metalType: "Gold",
      purity: "22K (916)",
      weight: "",
      pricingType: "dynamic",
      fixedPrice: "",
      makingCharge: "",
      makingChargeType: "per_gram",
      gstPercent: 3,
      stockStatus: "in_stock",
      bookingEnabled: true,
      bookingAmount: "",
      description: "",
    };
  }

  try {
    const data = JSON.parse(rawMsg);
    if (data && typeof data === "object") {
      return {
        metalType: data.metalType || "Gold",
        purity: data.purity || "22K (916)",
        weight: data.weight !== undefined ? data.weight : "",
        pricingType: data.pricingType || "dynamic",
        fixedPrice: data.fixedPrice !== undefined ? data.fixedPrice : "",
        makingCharge: data.makingCharge !== undefined ? data.makingCharge : "",
        makingChargeType: data.makingChargeType || "per_gram",
        gstPercent: data.gstPercent !== undefined ? data.gstPercent : 3,
        stockStatus: data.stockStatus || "in_stock",
        bookingEnabled: data.bookingEnabled !== undefined ? data.bookingEnabled : true,
        bookingAmount: data.bookingAmount !== undefined ? data.bookingAmount : "",
        description: data.description || "",
      };
    }
  } catch (_) {}

  // Fallback for legacy format
  const lines = rawMsg.split("\n");
  if (lines.length >= 2) {
    const first = lines[0];
    const match = first.match(/^([A-Za-z]+)\s*·\s*~(\d+(?:\.\d+)?)g$/);
    if (match) {
      return {
        metalType: match[1],
        purity: match[1] === "Gold" ? "22K (916)" : "Sterling (92.5)",
        weight: match[2],
        pricingType: "dynamic",
        fixedPrice: "",
        makingCharge: "",
        makingChargeType: "per_gram",
        gstPercent: 3,
        stockStatus: "in_stock",
        bookingEnabled: true,
        bookingAmount: "",
        description: lines.slice(1).join("\n"),
      };
    }
    const metalMatch = METAL_TYPES.find(m => m.value === first.trim());
    if (metalMatch) {
      return {
        metalType: metalMatch.value,
        purity: metalMatch.value === "Gold" ? "22K (916)" : "Sterling (92.5)",
        weight: "",
        pricingType: "dynamic",
        fixedPrice: "",
        makingCharge: "",
        makingChargeType: "per_gram",
        gstPercent: 3,
        stockStatus: "in_stock",
        bookingEnabled: true,
        bookingAmount: "",
        description: lines.slice(1).join("\n"),
      };
    }
  }
  return {
    metalType: "Gold",
    purity: "22K (916)",
    weight: "",
    pricingType: "dynamic",
    fixedPrice: "",
    makingCharge: "",
    makingChargeType: "per_gram",
    gstPercent: 3,
    stockStatus: "in_stock",
    bookingEnabled: true,
    bookingAmount: "",
    description: rawMsg,
  };
}

// ─── Price Calculator Helper ──────────────────────────────────────────────────
function calculateJewelryPrice({ weight, pricingType, fixedPrice, makingCharge, makingChargeType, gstPercent, goldRate }) {
  const w = parseFloat(weight) || 0;
  const rate = parseFloat(goldRate) || 0;
  const gst = parseFloat(gstPercent) || 3;

  let baseValue = 0;
  if (pricingType === "fixed") {
    baseValue = parseFloat(fixedPrice) || 0;
  } else {
    baseValue = w * rate;
  }

  let mc = parseFloat(makingCharge) || 0;
  let mcTotal = 0;
  if (makingChargeType === "per_gram") {
    mcTotal = mc * w;
  } else if (makingChargeType === "percentage") {
    mcTotal = baseValue * (mc / 100);
  } else {
    mcTotal = mc; // flat
  }

  const subtotal = baseValue + mcTotal;
  const tax = subtotal * (gst / 100);
  const total = subtotal + tax;

  return {
    baseValue: Math.round(baseValue),
    makingCharges: Math.round(mcTotal),
    gstAmount: Math.round(tax),
    total: Math.round(total),
  };
}

// ─── App-style live preview card ─────────────────────────────────────────────────
function ArrivalPreviewCard({
  title, metalType, weight, description, imageUrl, isActive,
  purity, pricingType, fixedPrice, makingCharge, makingChargeType, gstPercent,
  stockStatus, bookingEnabled, bookingAmount, goldRate = 7200
}) {
  const metalCfg = METAL_TYPES.find(m => m.value === metalType) || METAL_TYPES[0];
  const hasContent = title || description || imageUrl;

  const priceDetails = calculateJewelryPrice({
    weight, pricingType, fixedPrice, makingCharge, makingChargeType, gstPercent, goldRate
  });

  const getStockStatusStyle = (status) => {
    switch(status) {
      case "in_stock": return { bg: "#065f46", text: "#34d399", label: "In Stock" };
      case "pre_order": return { bg: "#7c2d12", text: "#fb923c", label: "Pre-Order" };
      default: return { bg: "#4b5563", text: "#9ca3af", label: "Sold Out" };
    }
  };

  const stockCfg = getStockStatusStyle(stockStatus);

  return (
    <div className="rounded-xl overflow-hidden border border-slate-700/60 bg-[#111827] shadow-xl flex flex-col">
      {/* preview header */}
      <div className="px-3 py-1.5 bg-[#0d1117] border-b border-slate-700/50 flex items-center gap-1.5">
        <FiEye className="w-3 h-3 text-slate-400" />
        <span className="text-[9.5px] text-slate-400 font-semibold uppercase tracking-widest" style={INTER}>Live Preview · SherGoldApp</span>
      </div>

      {!hasContent ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 py-8 px-4">
          <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
            <FiPackage className="w-5 h-5 text-slate-600" />
          </div>
          <p className="text-[10px] text-slate-500 text-center" style={INTER}>Fill in the form to see a live preview</p>
        </div>
      ) : (
        <div className="p-3 flex flex-col gap-2.5">
          {/* Card header row */}
          <div className="flex items-start gap-2.5">
            {/* image or placeholder */}
            <div className="w-16 h-16 rounded-lg bg-slate-800 border border-slate-700 flex-shrink-0 overflow-hidden">
              {imageUrl ? (
                <img src={imageUrl} alt="product" className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FiImage className="w-5 h-5 text-slate-600" />
                </div>
              )}
            </div>
            {/* title & badges */}
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-bold text-white leading-tight truncate" style={INTER}>
                {title || "Product Name"}
              </p>
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                <span
                  className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: metalCfg.bg, color: metalCfg.color, border: `1px solid ${metalCfg.border}` }}
                >
                  {metalType} ({purity || "22K"})
                </span>
                {weight && (
                  <span className="text-[9px] text-slate-400 font-medium" style={INTER}>~{weight}g</span>
                )}
                <span className="text-[8.5px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: stockCfg.bg, color: stockCfg.text }}>
                  {stockCfg.label}
                </span>
              </div>
            </div>
          </div>

          {/* Pricing Box */}
          <div className="bg-[#1f2937]/50 rounded-lg p-2 border border-slate-700/40">
            <div className="flex justify-between items-center mb-1">
              <span className="text-[9.5px] text-slate-400 font-medium" style={INTER}>Estimated Price</span>
              <span className="text-[13px] font-extrabold text-[#FEBB1B]" style={INTER}>
                ₹{priceDetails.total.toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex flex-col gap-0.5 pt-1 border-t border-slate-700/30 text-[8.5px] text-slate-400">
              <div className="flex justify-between">
                <span>{pricingType === "fixed" ? "Fixed Price:" : `Metal Value (${weight || 0}g @ ₹${goldRate}):`}</span>
                <span>₹{priceDetails.baseValue.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between">
                <span>Making Charges ({makingChargeType === "per_gram" ? "per g" : makingChargeType === "percentage" ? "%" : "flat"}):</span>
                <span>₹{priceDetails.makingCharges.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between">
                <span>GST ({gstPercent}%):</span>
                <span>₹{priceDetails.gstAmount.toLocaleString("en-IN")}</span>
              </div>
            </div>
          </div>

          {/* Booking Info & Action Preview */}
          {bookingEnabled && stockStatus !== "out_of_stock" && (
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 flex flex-col leading-none">
                <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider">Booking Deposit</span>
                <span className="text-[10px] text-white font-extrabold mt-0.5">₹{(bookingAmount || 0).toLocaleString("en-IN")}</span>
              </div>
              <div className="px-3 py-1 bg-emerald-600 rounded text-center text-white text-[9.5px] font-extrabold flex items-center gap-1 select-none">
                <FiPackage className="w-2.5 h-2.5" /> Book Now
              </div>
            </div>
          )}

          {/* status chip */}
          <div className="flex items-center gap-1">
            <div className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-slate-600"}`} />
            <span className={`text-[9px] font-semibold ${isActive ? "text-emerald-400" : "text-slate-500"}`} style={INTER}>
              {isActive ? "Visible to customers" : "Hidden from customers"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────────
function AppBroadcastManager({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();
  const permissions = useSelector(selectMenuPermissions);

  const formPermissions = permissions?.["appBroadcastManager"]?.actions || {};
  const hasSavePermission = formPermissions.Save ?? true;
  const hasEditPermission = formPermissions.Edit ?? true;
  const hasDeletePermission = formPermissions.Delete ?? true;
  const hasFindPermission = formPermissions.Find ?? true;

  // ── Tab state ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("notifications"); // "notifications" | "arrivals"

  // ── Shared data ──────────────────────────────────────────────────────────────
  const [notifications, setNotifications]     = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);

  // ── Notifications tab state ──────────────────────────────────────────────────
  const [notifForm, setNotifForm]         = useState(EMPTY_NOTIF_FORM);
  const [notifEditing, setNotifEditing]   = useState(false);
  const [notifSending, setNotifSending]   = useState(false);
  const [notifSearch, setNotifSearch]     = useState("");
  const [notifFilterCat, setNotifFilterCat] = useState("All");

  // ── Arrivals tab state ───────────────────────────────────────────────────────
  const [arrivalForm, setArrivalForm]         = useState(EMPTY_ARRIVAL_FORM);
  const [arrivalEditing, setArrivalEditing]   = useState(false);
  const [arrivalSending, setArrivalSending]   = useState(false);
  const [arrivalSearch, setArrivalSearch]     = useState("");
  const [arrivalImages, setArrivalImages]     = useState([]);   // { Auto, ImageUrl, SortOrder } | { _tempId, ImageUrl, SortOrder }
  const [newImageUrl, setNewImageUrl]         = useState("");   // URL input for add-image field
  const [goldRate, setGoldRate]               = useState(7200);
  const [draftArrivals, setDraftArrivals]     = useState([]);   // staging area queue list for bulk posting

  // refs
  const notifTitleRef      = useRef(null);
  const notifMessageRef    = useRef(null);
  const notifActionUrlRef  = useRef(null);
  const notifExpiresAtRef  = useRef(null);
  const notifImageUrlRef   = useRef(null);

  const arrivalTitleRef            = useRef(null);
  const arrivalPurityRef           = useRef(null);
  const arrivalWeightRef           = useRef(null);
  const arrivalFixedPriceRef       = useRef(null);
  const arrivalMakingChargeRef     = useRef(null);
  const arrivalMakingChargeTypeRef = useRef(null);
  const arrivalGstPercentRef       = useRef(null);
  const arrivalStockStatusRef      = useRef(null);
  const arrivalBookingAmountRef    = useRef(null);
  const arrivalMessageRef          = useRef(null);
  const arrivalImageUrlRef         = useRef(null);

  const handleEnterKey = (e, nextRef) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
        if (typeof nextRef.current.select === "function") {
          nextRef.current.select();
        }
      }
    }
  };

  // ── Fetch notification list ──────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    if (!dbCode) return;
    try {
      const res = await axios.get(`${apiBaseUrl}/main/appnotification/list?dbCode=${dbCode}`);
      if (res.data.success) setNotifications(res.data.data || []);
    } catch (err) {
      console.error("Error fetching app notifications:", err.message);
    }
  }, [apiBaseUrl, dbCode]);

  // ── Fetch customers ──────────────────────────────────────────────────────────
  const fetchCustomers = useCallback(async () => {
    if (!dbCode) return;
    try {
      const res = await axios.get(`${apiBaseUrl}/main/SchRec_Cname/${dbCode}`);
      const opts = (res.data || []).map(item => ({ label: item.LedName, value: item.Ledcode }));
      setCustomerOptions(opts);
    } catch (_) {}
  }, [apiBaseUrl, dbCode]);

  // ── Fetch current gold rate ──────────────────────────────────────────────────
  const fetchGoldRate = useCallback(async () => {
    if (!dbCode) return;
    try {
      const res = await axios.get(`${apiBaseUrl}/main/currentgoldRate/${dbCode}`);
      if (res.data && res.data[0]) {
        setGoldRate(res.data[0].currentRate || 7200);
      }
    } catch (_) {}
  }, [apiBaseUrl, dbCode]);

  useEffect(() => {
    fetchList();
    fetchCustomers();
    fetchGoldRate();
  }, [fetchList, fetchCustomers, fetchGoldRate]);

  // ── NOTIFICATIONS form helpers ───────────────────────────────────────────────
  const handleNotifChange = (field, value) => setNotifForm(prev => ({ ...prev, [field]: value }));

  const clearNotifForm = () => {
    setNotifForm(EMPTY_NOTIF_FORM);
    setNotifEditing(false);
    notifTitleRef.current?.focus();
  };

  const handleNotifEdit = (item) => {
    if (!hasEditPermission) {
      alert("Permission Denied: You do not have permission to edit notifications.");
      return;
    }
    const matchedCust = customerOptions.find(c => c.value === item.TargetCustomerValue);
    setNotifForm({
      Auto: item.Auto,
      Category: item.Category || "General",
      Title: item.Title || "",
      Message: item.Message || "",
      ImageUrl: item.ImageUrl || "",
      ActionUrl: item.ActionUrl || "",
      IsActive: item.IsActive,
      ExpiresAt: item.ExpiresAt ? item.ExpiresAt.substring(0, 10) : "",
      Priority: item.Priority || 0,
      TargetCustomerValue: item.TargetCustomerValue || "",
      TargetCustomerLabel: matchedCust?.label || "",
      TargetLocation: item.TargetLocation || "",
    });
    setNotifEditing(true);
    notifTitleRef.current?.focus();
  };

  const handleNotifSave = async () => {
    if (notifEditing && !hasEditPermission) {
      alert("Permission Denied: You do not have permission to edit notifications.");
      return;
    }
    if (!notifEditing && !hasSavePermission) {
      alert("Permission Denied: You do not have permission to save new notifications.");
      return;
    }
    if (!notifForm.Title.trim()) return alert("Title is required.");
    if (!notifForm.Message.trim()) return alert("Message is required.");
    const payload = {
      Auto: notifForm.Auto,
      Category: notifForm.Category,
      Title: notifForm.Title.trim(),
      Message: notifForm.Message.trim(),
      ImageUrl: formatImageUrl(notifForm.ImageUrl) || null,
      ActionUrl: notifForm.ActionUrl.trim() || null,
      IsActive: notifForm.IsActive ? 1 : 0,
      ExpiresAt: notifForm.ExpiresAt || null,
      Priority: parseInt(notifForm.Priority) || 0,
      TargetCustomerValue: notifForm.TargetCustomerValue ? parseInt(notifForm.TargetCustomerValue) : null,
      TargetLocation: notifForm.TargetLocation ? parseInt(notifForm.TargetLocation) : null,
      dbCode,
    };
    setNotifSending(true);
    try {
      const res = await axios.post(`${apiBaseUrl}/main/appnotification/save`, payload);
      if (res.data.success) {
        alert(notifEditing ? "Notification updated!" : "Notification saved and sent to customers!");
        clearNotifForm();
        fetchList();
      } else {
        alert(res.data.message || "Save failed.");
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Save failed.");
    } finally {
      setNotifSending(false);
    }
  };

  // ── ARRIVALS form helpers ────────────────────────────────────────────────────
  const handleArrivalChange = (field, value) => setArrivalForm(prev => ({ ...prev, [field]: value }));

  const clearArrivalForm = () => {
    setArrivalForm(EMPTY_ARRIVAL_FORM);
    setArrivalEditing(false);
    setArrivalImages([]);
    setNewImageUrl("");
    arrivalTitleRef.current?.focus();
  };

  const handleArrivalEdit = async (item) => {
    if (!hasEditPermission) {
      alert("Permission Denied: You do not have permission to edit arrivals.");
      return;
    }
    const parsed = parseArrivalMessage(item.Message);
    setArrivalForm({
      Auto: item.Auto,
      Category: "Arrival",
      Title: item.Title || "",
      MetalType: parsed.metalType,
      Purity: parsed.purity,
      Weight: parsed.weight,
      PricingType: parsed.pricingType,
      FixedPrice: parsed.fixedPrice,
      MakingCharge: parsed.makingCharge,
      MakingChargeType: parsed.makingChargeType,
      GstPercent: parsed.gstPercent,
      StockStatus: parsed.stockStatus,
      BookingEnabled: parsed.bookingEnabled,
      BookingAmount: parsed.bookingAmount,
      Message: parsed.description,
      IsActive: item.IsActive,
      ExpiresAt: item.ExpiresAt ? item.ExpiresAt.substring(0, 10) : "",
      Priority: item.Priority || 0,
      TargetCustomerValue: item.TargetCustomerValue || "",
      TargetCustomerLabel: customerOptions.find(c => c.value === item.TargetCustomerValue)?.label || "",
    });
    setArrivalEditing(true);
    setNewImageUrl("");
    // Load existing images from API
    try {
      const res = await axios.get(`${apiBaseUrl}/main/appnotification/${item.Auto}/images?dbCode=${dbCode}`);
      if (res.data.success) setArrivalImages(res.data.data || []);
    } catch (_) { setArrivalImages([]); }
    arrivalTitleRef.current?.focus();
  };

  const handleArrivalSave = async () => {
    if (arrivalEditing && !hasEditPermission) {
      alert("Permission Denied: You do not have permission to edit arrivals.");
      return;
    }
    if (!arrivalEditing && !hasSavePermission) {
      alert("Permission Denied: You do not have permission to post new arrivals.");
      return;
    }
    if (!arrivalForm.Title.trim()) return alert("Product name is required.");
    const composedMsg = composeArrivalMessage(arrivalForm);
    if (!composedMsg) return alert("Please add a description.");
    const payload = {
      Auto: arrivalForm.Auto,
      Category: "Arrival",
      Title: arrivalForm.Title.trim(),
      Message: composedMsg,
      ImageUrl: arrivalImages[0]?.ImageUrl || null,  // first image as legacy cover
      ActionUrl: null,
      IsActive: arrivalForm.IsActive ? 1 : 0,
      ExpiresAt: arrivalForm.ExpiresAt || null,
      Priority: parseInt(arrivalForm.Priority) || 0,
      TargetCustomerValue: arrivalForm.TargetCustomerValue ? parseInt(arrivalForm.TargetCustomerValue) : null,
      TargetLocation: null,
      dbCode,
    };
    setArrivalSending(true);
    try {
      const res = await axios.post(`${apiBaseUrl}/main/appnotification/save`, payload);
      if (res.data.success) {
        const savedId = res.data.data?.Auto || arrivalForm.Auto;
        // Upload any queued (temp) images that don't have a real Auto yet
        if (savedId) {
          const tempImages = arrivalImages.filter(img => img._tempId);
          for (let i = 0; i < tempImages.length; i++) {
            try {
              await axios.post(`${apiBaseUrl}/main/appnotification/${savedId}/images`, {
                imageUrl: tempImages[i].ImageUrl,
                sortOrder: i,
                dbCode,
              });
            } catch (_) {}
          }
        }
        alert(arrivalEditing ? "Arrival updated!" : "New arrival posted to customers!");
        clearArrivalForm();
        fetchList();
      } else {
        alert(res.data.message || "Save failed.");
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Save failed.");
    } finally {
      setArrivalSending(false);
    }
  };

  const handleAddToQueue = () => {
    if (!hasSavePermission) {
      alert("Permission Denied: You do not have permission to queue new arrivals.");
      return;
    }
    if (!arrivalForm.Title.trim()) return alert("Product name is required.");
    const composedMsg = composeArrivalMessage(arrivalForm);
    if (!composedMsg) return alert("Please add a description.");

    const draftItem = {
      _tempDraftId: Date.now(),
      form: { ...arrivalForm },
      images: [...arrivalImages],
    };

    setDraftArrivals(prev => [...prev, draftItem]);
    clearArrivalForm();
  };

  const handleRemoveFromQueue = (tempId) => {
    setDraftArrivals(prev => prev.filter(item => item._tempDraftId !== tempId));
  };

  const handlePostQueue = async () => {
    if (!hasSavePermission) {
      alert("Permission Denied: You do not have permission to post arrivals.");
      return;
    }
    if (draftArrivals.length === 0) return;
    if (!window.confirm(`Post all ${draftArrivals.length} queued items to the app?`)) return;

    setArrivalSending(true);
    let successCount = 0;

    for (const draft of draftArrivals) {
      const { form, images } = draft;
      const composedMsg = composeArrivalMessage(form);
      const payload = {
        Auto: 0,
        Category: "Arrival",
        Title: form.Title.trim(),
        Message: composedMsg,
        ImageUrl: images[0]?.ImageUrl || null,
        ActionUrl: null,
        IsActive: form.IsActive ? 1 : 0,
        ExpiresAt: form.ExpiresAt || null,
        Priority: parseInt(form.Priority) || 0,
        TargetCustomerValue: form.TargetCustomerValue ? parseInt(form.TargetCustomerValue) : null,
        TargetLocation: null,
        dbCode,
      };

      try {
        const res = await axios.post(`${apiBaseUrl}/main/appnotification/save`, payload);
        if (res.data.success) {
          const savedId = res.data.data?.Auto;
          if (savedId && images.length > 0) {
            for (let i = 0; i < images.length; i++) {
              try {
                await axios.post(`${apiBaseUrl}/main/appnotification/${savedId}/images`, {
                  imageUrl: images[i].ImageUrl,
                  sortOrder: i,
                  dbCode,
                });
              } catch (_) {}
            }
          }
          successCount++;
        }
      } catch (err) {
        console.error("Failed to post draft:", form.Title, err.message);
      }
    }

    setArrivalSending(false);
    setDraftArrivals([]);
    fetchList();
    alert(`Successfully posted ${successCount} of ${draftArrivals.length} items to the app!`);
  };

  // ── Image management handlers ───────────────────────────────────────
  const handleAddImage = async () => {
    if (arrivalForm.Auto && arrivalEditing && !hasEditPermission) {
      alert("Permission Denied: You do not have permission to add images to saved arrivals.");
      return;
    }
    if (!arrivalEditing && !hasSavePermission) {
      alert("Permission Denied: You do not have permission to add images.");
      return;
    }
    const url = formatImageUrl(newImageUrl);
    if (!url) return;
    if (arrivalImages.length >= MAX_IMAGES) return alert(`Max ${MAX_IMAGES} images allowed.`);

    if (arrivalForm.Auto && arrivalEditing) {
      // Arrival already saved — call API directly
      try {
        const res = await axios.post(`${apiBaseUrl}/main/appnotification/${arrivalForm.Auto}/images`, {
          imageUrl: url,
          sortOrder: arrivalImages.length,
          dbCode,
        });
        if (res.data.success) {
          setArrivalImages(prev => [...prev, { Auto: res.data.data.Auto, ImageUrl: url, SortOrder: arrivalImages.length }]);
        }
      } catch (err) {
        alert(err.response?.data?.message || "Failed to add image.");
        return;
      }
    } else {
      // New arrival not yet saved — queue locally with a temp id
      setArrivalImages(prev => [...prev, { _tempId: Date.now(), ImageUrl: url, SortOrder: prev.length }]);
    }
    setNewImageUrl("");
  };

  const handleDeleteImage = async (idx, img) => {
    if (!hasDeletePermission && !hasEditPermission) {
      alert("Permission Denied: You do not have permission to delete images.");
      return;
    }
    if (img.Auto) {
      // Persisted image — call delete API
      try {
        await axios.delete(`${apiBaseUrl}/main/appnotification/images/${img.Auto}?dbCode=${dbCode}`);
      } catch (err) {
        alert("Delete image failed: " + err.message);
        return;
      }
    }
    // Remove from local state and reassign SortOrders
    setArrivalImages(prev => prev.filter((_, i) => i !== idx).map((im, i) => ({ ...im, SortOrder: i })));
  };

  const handleMoveImage = async (idx, direction) => {
    const newImages = [...arrivalImages];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= newImages.length) return;
    [newImages[idx], newImages[swapIdx]] = [newImages[swapIdx], newImages[idx]];
    // Reassign SortOrders
    const updated = newImages.map((img, i) => ({ ...img, SortOrder: i }));
    setArrivalImages(updated);
    // Persist reorder for saved images
    if (arrivalForm.Auto && arrivalEditing) {
      for (const img of updated) {
        if (img.Auto) {
          try {
            await axios.put(`${apiBaseUrl}/main/appnotification/images/${img.Auto}/reorder`, { sortOrder: img.SortOrder, dbCode });
          } catch (_) {}
        }
      }
    }
  };

  // ── Shared: Delete & Toggle ──────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!hasDeletePermission) {
      alert("Permission Denied: You do not have permission to delete broadcast items.");
      return;
    }
    if (!window.confirm("Delete this item? It will be removed from all customer feeds.")) return;
    try {
      const res = await axios.delete(`${apiBaseUrl}/main/appnotification/${id}?dbCode=${dbCode}`);
      if (res.data.success) { fetchList(); }
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const handleToggle = async (id) => {
    if (!hasEditPermission) {
      alert("Permission Denied: You do not have permission to toggle item status.");
      return;
    }
    try {
      await axios.put(`${apiBaseUrl}/main/appnotification/${id}/toggle?dbCode=${dbCode}`);
      fetchList();
    } catch (err) {
      alert("Toggle failed: " + err.message);
    }
  };

  // ── Computed ─────────────────────────────────────────────────────────────────
  const arrivals     = notifications.filter(n => n.Category === "Arrival");
  const nonArrivals  = notifications.filter(n => n.Category !== "Arrival");

  const filteredNotifs = notifications.filter(n => {
    const matchSearch = n.Title?.toLowerCase().includes(notifSearch.toLowerCase()) ||
                        n.Message?.toLowerCase().includes(notifSearch.toLowerCase());
    const matchCat = notifFilterCat === "All" || n.Category === notifFilterCat;
    return matchSearch && matchCat;
  });

  const filteredArrivals = arrivals.filter(n =>
    n.Title?.toLowerCase().includes(arrivalSearch.toLowerCase()) ||
    n.Message?.toLowerCase().includes(arrivalSearch.toLowerCase())
  );

  const totalCount     = notifications.length;
  const activeCount    = notifications.filter(n => n.IsActive).length;
  const arrivalCount   = arrivals.length;
  const broadcastCount = notifications.filter(n => !n.TargetCustomerValue).length;

  const isNotifBroadcast   = !notifForm.TargetCustomerValue;
  const isArrivalBroadcast = !arrivalForm.TargetCustomerValue;

  // live preview values
  const previewMsg = composeArrivalMessage(arrivalForm);
  // eslint-disable-next-line no-unused-vars
  const { description: previewDesc } = parseArrivalMessage(previewMsg);

  return (
    <div
      className="absolute inset-0 w-full h-full bg-[#f0f0f0] flex items-center justify-center pointer-events-auto select-none"
      style={{ zIndex: zIndex || 101 }}
    >
      <div className="bg-white font-normal w-[99%] max-w-[1440px] h-[99%] max-h-[900px] border border-slate-200 shadow-md flex flex-col overflow-hidden absolute">

        {/* ── Window Header ───────────────────────────────────────────────────── */}
        <WindowHeader
          title="App Broadcast Manager"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#0f2d59] text-white h-9 px-3 shrink-0"
          closeBtnClassName="h-6 w-6 my-auto mr-1 bg-red-600 hover:bg-red-700 rounded-sm border-none flex items-center justify-center text-white"
        />

        {/* ── Top Toolbar ─────────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-slate-200/80 px-4 py-2 shrink-0 flex flex-nowrap items-center justify-between gap-2.5 overflow-x-auto">
          <div className="flex items-center gap-2.5 shrink-0">
            <div className="w-11 h-11 bg-[#0d2347] rounded-full flex items-center justify-center text-white shrink-0 shadow-sm">
              <FiBell className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <h1 className="text-[17px] font-bold text-slate-800 leading-tight" style={INTER}>App Broadcast Manager</h1>
              <p className="text-[11.5px] text-slate-500 font-medium" style={INTER}>Send offers, arrivals and alerts to customers on SherGoldApp</p>
            </div>
          </div>
          {/* Stat Cards */}
          <div className="flex items-center gap-2.5 ml-auto shrink-0">
            <StatCard icon={FiBell}        label="Total"     value={totalCount}    color="#0f2d59" />
            <StatCard icon={FiCheckCircle} label="Active"    value={activeCount}   color="#15803d" />
            <StatCard icon={FiPackage}     label="Arrivals"  value={arrivalCount}  color="#15803d" />
            <StatCard icon={FiUsers}       label="Broadcast" value={broadcastCount} color="#1d4ed8" />
          </div>
        </div>

        {/* ── Tab Switcher ─────────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-slate-200 px-4 pt-0 shrink-0 flex items-end gap-0">
          {/* Notifications tab */}
          <button
            type="button"
            onClick={() => setActiveTab("notifications")}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-bold cursor-pointer border-b-2 transition-all select-none outline-none bg-transparent ${
              activeTab === "notifications"
                ? "border-[#0f2d59] text-[#0f2d59]"
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300"
            }`}
            style={INTER}
          >
            <FiBell className="w-3.5 h-3.5" />
            Notifications
            <span className={`ml-1 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full ${activeTab === "notifications" ? "bg-[#0f2d59] text-white" : "bg-slate-100 text-slate-500"}`}>
              {nonArrivals.length}
            </span>
          </button>

          {/* Arrivals tab */}
          <button
            type="button"
            onClick={() => setActiveTab("arrivals")}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-[12px] font-bold cursor-pointer border-b-2 transition-all select-none outline-none bg-transparent ${
              activeTab === "arrivals"
                ? "border-emerald-600 text-emerald-700"
                : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300"
            }`}
            style={INTER}
          >
            <FiPackage className="w-3.5 h-3.5" />
            New Arrivals
            <span className={`ml-1 text-[9px] font-extrabold px-1.5 py-0.5 rounded-full ${activeTab === "arrivals" ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>
              {arrivalCount}
            </span>
            {arrivalCount > 0 && activeTab !== "arrivals" && (
              <span className="absolute -top-0.5 right-0 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            )}
          </button>
        </div>

        {/* ── Main Body ───────────────────────────────────────────────────────── */}
        <div className="flex-1 flex gap-4 p-4 min-h-0 bg-slate-50 overflow-hidden">

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* NOTIFICATIONS TAB                                                  */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          {activeTab === "notifications" && (
            <>
              {/* LEFT: Notification Form */}
              <div className="w-[33%] bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden shrink-0 justify-between">
                <div className="flex flex-col gap-2.5 p-3 flex-1 min-h-0">

                  {/* Form header banner */}
                  <div className="bg-gradient-to-r from-[#051126] via-[#0b2246] to-[#143260] rounded-t-xl py-2 px-3 text-white flex items-center gap-2.5 relative overflow-hidden shadow-xs shrink-0 select-none min-h-[38px]">
                    <div className="relative flex items-center justify-center shrink-0">
                      <FiBell className="w-5 h-5 text-amber-300 stroke-[1.5]" />
                      {!notifEditing && <span className="absolute right-[-4px] bottom-[-4px] text-amber-300 font-extrabold text-[12px]">+</span>}
                    </div>
                    <div className="z-10 leading-tight">
                      <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={INTER}>
                        {notifEditing ? "Edit Notification" : "Create Notification"}
                      </h3>
                      <p className="text-[9px] text-slate-300 font-medium leading-none mt-0.5" style={INTER}>
                        {isNotifBroadcast ? "Shop-wide broadcast → all customers" : "Personal message → specific customer"}
                      </p>
                    </div>
                    {/* Golden slash design block on the far right (Precise SVG layout matching CustomerAppReg) */}
                    <div className="absolute right-0 top-0 h-full w-32 pointer-events-none rounded-tr-xl overflow-hidden">
                      <svg 
                        className="absolute right-0 top-0 h-full w-full pointer-events-none" 
                        viewBox="0 0 120 44" 
                        preserveAspectRatio="none"
                      >
                        <defs>
                          {/* Metallic Gold Gradient */}
                          <linearGradient id="goldGradBm" x1="0%" y1="100%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#a67c1e" />
                            <stop offset="30%" stopColor="#f1c40f" />
                            <stop offset="50%" stopColor="#fbf3b9" />
                            <stop offset="70%" stopColor="#f1c40f" />
                            <stop offset="100%" stopColor="#aa7c11" />
                          </linearGradient>
                          {/* Honeycomb Pattern */}
                          <pattern id="honeycombBm" width="6" height="6" patternUnits="userSpaceOnUse">
                            <path d="M 3 0 L 6 1.5 L 6 4.5 L 3 6 L 0 4.5 L 0 1.5 Z" fill="none" stroke="#aa7c11" strokeWidth="0.4" opacity="0.35" />
                          </pattern>
                        </defs>
                        
                        {/* Bottom-right gold patterned triangle */}
                        <polygon points="78,44 120,44 120,14" fill="url(#goldGradBm)" />
                        <polygon points="78,44 120,44 120,14" fill="url(#honeycombBm)" />
                        
                        {/* Inner/Right Gold diagonal line */}
                        <line x1="78" y1="44" x2="120" y2="14" stroke="url(#goldGradBm)" strokeWidth="2.2" />
                        
                        {/* Outer/Left Gold diagonal line */}
                        <line x1="66" y1="44" x2="108" y2="14" stroke="url(#goldGradBm)" strokeWidth="1.5" />
                      </svg>
                    </div>
                  </div>

                  {/* Form Fields */}
                  <div className="flex flex-col gap-2 overflow-y-auto pr-0.5 flex-1">

                    {/* Target */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 flex flex-col gap-1.5">
                      <label className="text-[10.5px] font-bold text-slate-600 uppercase tracking-wider" style={INTER}>Target Audience</label>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => handleNotifChange("TargetCustomerValue", "")}
                          className={`flex-1 h-8 rounded-md text-[11px] font-bold border flex items-center justify-center gap-1.5 cursor-pointer transition-all ${isNotifBroadcast ? "bg-[#0f2d59] text-white border-[#0f2d59]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                          <FiUsers className="w-3.5 h-3.5" /> All Customers
                        </button>
                        <button type="button" onClick={() => { if (isNotifBroadcast) handleNotifChange("TargetCustomerValue", "_pick"); }}
                          className={`flex-1 h-8 rounded-md text-[11px] font-bold border flex items-center justify-center gap-1.5 cursor-pointer transition-all ${!isNotifBroadcast ? "bg-[#0f2d59] text-white border-[#0f2d59]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                          <FiUser className="w-3.5 h-3.5" /> Specific Customer
                        </button>
                      </div>
                      {!isNotifBroadcast && (
                        <select value={notifForm.TargetCustomerValue} onChange={e => handleNotifChange("TargetCustomerValue", e.target.value)}
                          className="w-full h-[28px] px-2 border border-slate-200 rounded-md text-slate-800 text-[11px] outline-none hover:border-slate-300 focus:border-[#0f2d59] bg-white" style={INTER}>
                          <option value="_pick">— Select customer —</option>
                          {customerOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      )}
                    </div>

                    {/* Category */}
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiTag className="w-3 h-3 text-slate-400" /> Category <span className="text-rose-500">*</span>
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {CATEGORIES.map(cat => {
                          const Icon = cat.icon;
                          const active = notifForm.Category === cat.value;
                          return (
                            <button key={cat.value} type="button" onClick={() => handleNotifChange("Category", cat.value)}
                              style={{ backgroundColor: active ? cat.bg : undefined, borderColor: active ? cat.border : undefined, color: active ? cat.color : undefined }}
                              className={`flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-semibold cursor-pointer transition-all ${active ? "border-2" : "border border-slate-200 text-slate-500 hover:border-slate-300 bg-white"}`}>
                              <Icon className="w-3 h-3" />{cat.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Title */}
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiBell className="w-3 h-3 text-slate-400" /> Notification Title <span className="text-rose-500">*</span>
                      </label>
                      <input type="text" ref={notifTitleRef} value={notifForm.Title} onChange={e => handleNotifChange("Title", e.target.value)}
                        onKeyDown={e => handleEnterKey(e, notifMessageRef)}
                        maxLength={150} placeholder="E.g. Diwali 20% Off on Making Charges"
                        className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all" style={INTER} />
                      <span className="text-[9px] text-slate-400">{notifForm.Title.length}/150 characters</span>
                    </div>

                    {/* Message */}
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiMessageSquare className="w-3 h-3 text-slate-400" /> Message <span className="text-rose-500">*</span>
                      </label>
                      <textarea ref={notifMessageRef} value={notifForm.Message} onChange={e => handleNotifChange("Message", e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            handleEnterKey(e, notifExpiresAtRef);
                          }
                        }}
                        maxLength={1000} rows={3} placeholder="Write your message to customers here..."
                        className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all resize-none" style={INTER} />
                      <span className="text-[9px] text-slate-400">{notifForm.Message.length}/1000 characters</span>
                    </div>

                    {/* Priority */}
                    <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-200/60 select-none">
                      <div className="flex flex-col">
                        <span className="text-[11px] font-bold text-slate-700 flex items-center gap-1" style={INTER}>
                          <FiZap className="w-3 h-3 text-amber-500" /> High Priority
                        </span>
                        <span className="text-[9px] text-slate-400">Pinned at top of customer feed</span>
                      </div>
                      <button type="button" onClick={() => handleNotifChange("Priority", notifForm.Priority === 1 ? 0 : 1)}
                        className={`w-10 h-[22px] flex items-center rounded-full p-0.5 cursor-pointer transition-colors duration-300 outline-none border-none ${notifForm.Priority === 1 ? "bg-[#b78a39]" : "bg-slate-300"}`}>
                        <div className={`bg-white w-[18px] h-[18px] rounded-full shadow-md transform transition-transform duration-300 ${notifForm.Priority === 1 ? "translate-x-[18px]" : "translate-x-0"}`} />
                      </button>
                    </div>

                    {/* Active */}
                    <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-200/60 select-none">
                      <div className="flex flex-col">
                        <span className="text-[11px] font-bold text-slate-700" style={INTER}>Active / Visible</span>
                        <span className="text-[9px] text-slate-400">Inactive notifications are hidden from customers</span>
                      </div>
                      <button type="button" onClick={() => handleNotifChange("IsActive", !notifForm.IsActive)}
                        className={`w-10 h-[22px] flex items-center rounded-full p-0.5 cursor-pointer transition-colors duration-300 outline-none border-none ${notifForm.IsActive ? "bg-emerald-500" : "bg-slate-300"}`}>
                        <div className={`bg-white w-[18px] h-[18px] rounded-full shadow-md transform transition-transform duration-300 ${notifForm.IsActive ? "translate-x-[18px]" : "translate-x-0"}`} />
                      </button>
                    </div>

                    {/* Expiry */}
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiCalendar className="w-3 h-3 text-slate-400" /> Expiry Date <span className="text-slate-400 font-normal">(optional)</span>
                      </label>
                      <input type="date" ref={notifExpiresAtRef} value={notifForm.ExpiresAt} onChange={e => handleNotifChange("ExpiresAt", e.target.value)}
                        onKeyDown={e => handleEnterKey(e, notifImageUrlRef)}
                        className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all" style={INTER} />
                      <span className="text-[9px] text-slate-400">Leave empty for no expiry</span>
                    </div>

                    {/* Image URL */}
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                        <FiPackage className="w-3 h-3 text-slate-400" /> Image URL <span className="text-slate-400 font-normal">(optional)</span>
                      </label>
                      <input type="text" ref={notifImageUrlRef} value={notifForm.ImageUrl} onChange={e => handleNotifChange("ImageUrl", e.target.value)}
                        placeholder="https://... (optional product photo)"
                        className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-[#0f2d59] transition-all" style={INTER} />
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="grid grid-cols-2 gap-2.5 pt-2 border-t border-slate-100 shrink-0">
                    <button type="button" onClick={clearNotifForm}
                      className="h-8 border border-slate-300 hover:bg-slate-50 text-slate-600 font-bold rounded-md transition-colors cursor-pointer text-xs" style={INTER}>
                      Clear Form
                    </button>
                    <button type="button" onClick={handleNotifSave} disabled={notifSending || (notifEditing ? !hasEditPermission : !hasSavePermission)}
                      className={`h-8 font-bold rounded-md transition-colors shadow-sm flex items-center justify-center gap-1.5 text-xs ${
                        (notifEditing ? !hasEditPermission : !hasSavePermission)
                          ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                          : "bg-[#b78a39] hover:bg-[#a67c32] text-white cursor-pointer"
                      }`}
                      title={(notifEditing ? !hasEditPermission : !hasSavePermission) ? "Permission Denied" : ""}
                      style={INTER}>
                      <FiSend className="w-3.5 h-3.5" />
                      {notifSending ? "Sending..." : notifEditing ? "Update" : "Send Now"}
                    </button>
                  </div>
                </div>

                {/* Info footer */}
                <div className="m-2 p-2 bg-amber-50/70 border border-amber-200/60 rounded-md flex items-center gap-2 text-amber-800 shrink-0">
                  <FiInfo className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <p className="text-[10px] font-medium leading-tight" style={INTER}>
                    {isNotifBroadcast ? "This message will be visible to ALL customers of your shop in SherGoldApp." : "This message will be sent only to the selected customer."}
                  </p>
                </div>
              </div>

              {/* RIGHT: Notification Grid */}
              <div className="flex-1 bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden">
                <div className="p-3.5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-white shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center shadow-xs">
                      <FiBell className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-800 leading-none" style={INTER}>Sent Notifications</h3>
                      <span className="text-[11.5px] text-slate-400 font-medium mt-1 block" style={INTER}>Manage all notifications sent to customers</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={notifFilterCat} onChange={e => setNotifFilterCat(e.target.value)}
                      className="h-8 px-2 border border-slate-200 rounded-lg text-[11.5px] text-slate-700 outline-none hover:border-slate-300 bg-white" style={INTER}>
                      <option value="All">All Categories</option>
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <div className="relative flex items-center">
                      <FiSearch className="absolute left-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                      <input type="text" value={notifSearch} disabled={!hasFindPermission} onChange={e => setNotifSearch(e.target.value)} placeholder={!hasFindPermission ? "Find permission disabled..." : "Search notifications..."}
                        className={`h-8 pl-8 pr-3 border rounded-lg text-[11.5px] outline-none w-48 transition-all ${
                          !hasFindPermission ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed" : "border-slate-200 text-slate-700 hover:border-slate-300 focus:border-[#0f2d59]"
                        }`} style={INTER} />
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-auto">
                  <table className="w-full text-[11.5px] border-collapse">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-slate-50 border-b border-slate-200">
                        {["Category", "Title & Message", "Target", "Priority", "Status", "Expiry", "Created", "Actions"].map(h => (
                          <th key={h} className="px-3 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider text-[10px] whitespace-nowrap" style={INTER}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredNotifs.length === 0 ? (
                        <tr><td colSpan={8} className="text-center py-16 text-slate-400">
                          <FiBell className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                          <p className="font-semibold text-slate-400" style={INTER}>No notifications yet</p>
                          <p className="text-[11px] text-slate-300 mt-1" style={INTER}>Create your first broadcast using the form on the left.</p>
                        </td></tr>
                      ) : (
                        filteredNotifs.map(item => {
                          const catStyle = getCategoryStyle(item.Category);
                          const CatIcon  = catStyle.icon;
                          return (
                            <tr key={item.Auto} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold w-fit"
                                  style={{ backgroundColor: catStyle.bg, color: catStyle.color, border: `1px solid ${catStyle.border}` }}>
                                  <CatIcon className="w-2.5 h-2.5" />{item.Category}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 max-w-[220px]">
                                <p className="font-bold text-slate-800 truncate" style={INTER}>{item.Title}</p>
                                <p className="text-slate-400 truncate text-[10.5px]" style={INTER}>{item.Message}</p>
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap">
                                {item.TargetCustomerValue
                                  ? <span className="flex items-center gap-1 text-purple-700 font-semibold" style={INTER}><FiUser className="w-3 h-3" />Personal</span>
                                  : <span className="flex items-center gap-1 text-blue-600 font-semibold" style={INTER}><FiUsers className="w-3 h-3" />Broadcast</span>}
                              </td>
                              <td className="px-3 py-2.5">
                                {item.Priority === 1
                                  ? <span className="flex items-center gap-1 text-amber-600 font-bold text-[10px]"><FiZap className="w-3 h-3" />High</span>
                                  : <span className="text-slate-400 text-[10px]">Normal</span>}
                              </td>
                              <td className="px-3 py-2.5">
                                <button onClick={() => handleToggle(item.Auto)} disabled={!hasEditPermission} className={`flex items-center gap-1 border-none bg-transparent p-0 ${!hasEditPermission ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`} title={!hasEditPermission ? "Permission Denied" : "Click to toggle"}>
                                  {item.IsActive
                                    ? <><FiToggleRight className="w-4 h-4 text-emerald-500" /><span className="text-emerald-600 font-semibold text-[10px]" style={INTER}>Active</span></>
                                    : <><FiToggleLeft className="w-4 h-4 text-slate-400" /><span className="text-slate-400 font-semibold text-[10px]" style={INTER}>Inactive</span></>}
                                </button>
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap text-slate-500" style={INTER}>
                                {item.ExpiresAt ? fmtDate(item.ExpiresAt) : <span className="text-slate-300 text-[10px]">No expiry</span>}
                              </td>
                              <td className="px-3 py-2.5 whitespace-nowrap text-slate-500" style={INTER}>{fmtDate(item.CreatedDate)}</td>
                              <td className="px-3 py-2.5">
                                <div className="flex items-center gap-1.5">
                                  <button onClick={() => handleNotifEdit(item)} disabled={!hasEditPermission} className={`w-7 h-7 rounded-md flex items-center justify-center border transition-colors ${!hasEditPermission ? "bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed" : "bg-slate-100 hover:bg-blue-50 text-slate-500 hover:text-blue-600 border-slate-200 hover:border-blue-200 cursor-pointer"}`} title={!hasEditPermission ? "Permission Denied: Edit action disabled" : "Edit"}>
                                    <FiEdit3 className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => handleDelete(item.Auto)} disabled={!hasDeletePermission} className={`w-7 h-7 rounded-md flex items-center justify-center border transition-colors ${!hasDeletePermission ? "bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed" : "bg-slate-100 hover:bg-red-50 text-slate-500 hover:text-red-600 border-slate-200 hover:border-red-200 cursor-pointer"}`} title={!hasDeletePermission ? "Permission Denied: Delete action disabled" : "Delete"}>
                                    <FiTrash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 shrink-0 flex items-center justify-between">
                  <span className="text-[11px] text-slate-400 font-medium" style={INTER}>
                    Showing <strong>{filteredNotifs.length}</strong> of <strong>{totalCount}</strong> notifications
                  </span>
                </div>
              </div>
            </>
          )}

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* ARRIVALS TAB                                                        */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          {activeTab === "arrivals" && (
            <>
              {/* LEFT: Arrival Form */}
              <div className="w-[34%] bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden shrink-0">

                {/* Arrival Form Header Banner */}
                <div className="bg-gradient-to-r from-[#052e16] via-[#14532d] to-[#166534] py-2.5 px-3 text-white flex items-center gap-2.5 relative overflow-hidden shrink-0 select-none min-h-[42px]">
                  <div className="relative flex items-center justify-center shrink-0">
                    <FiPackage className="w-5 h-5 text-amber-300 stroke-[1.5]" />
                    {!arrivalEditing && <span className="absolute right-[-4px] bottom-[-4px] text-amber-300 font-extrabold text-[12px]">+</span>}
                  </div>
                  <div className="z-10 leading-tight">
                    <h3 className="text-[12px] font-bold uppercase tracking-wider text-[#ffd700]" style={{ ...INTER, textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
                      {arrivalEditing ? "Edit Arrival" : "Post New Arrival"}
                    </h3>
                    <p className="text-[9px] text-emerald-100/70 font-medium leading-none mt-0.5" style={INTER}>
                      {isArrivalBroadcast ? "Visible to all customers in Arrivals tab" : "Targeted arrival for specific customer"}
                    </p>
                  </div>
                  {/* Golden slash design block on the far right (Precise SVG layout matching CustomerAppReg) */}
                  <div className="absolute right-0 top-0 h-full w-32 pointer-events-none rounded-tr-xl overflow-hidden">
                    <svg 
                      className="absolute right-0 top-0 h-full w-full pointer-events-none" 
                      viewBox="0 0 120 44" 
                      preserveAspectRatio="none"
                    >
                      <defs>
                        {/* Metallic Gold Gradient */}
                        <linearGradient id="goldGradArr" x1="0%" y1="100%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#a67c1e" />
                          <stop offset="30%" stopColor="#f1c40f" />
                          <stop offset="50%" stopColor="#fbf3b9" />
                          <stop offset="70%" stopColor="#f1c40f" />
                          <stop offset="100%" stopColor="#aa7c11" />
                        </linearGradient>
                        {/* Honeycomb Pattern */}
                        <pattern id="honeycombArr" width="6" height="6" patternUnits="userSpaceOnUse">
                          <path d="M 3 0 L 6 1.5 L 6 4.5 L 3 6 L 0 4.5 L 0 1.5 Z" fill="none" stroke="#aa7c11" strokeWidth="0.4" opacity="0.35" />
                        </pattern>
                      </defs>
                      
                      {/* Bottom-right gold patterned triangle */}
                      <polygon points="78,44 120,44 120,14" fill="url(#goldGradArr)" />
                      <polygon points="78,44 120,44 120,14" fill="url(#honeycombArr)" />
                      
                      {/* Inner/Right Gold diagonal line */}
                      <line x1="78" y1="44" x2="120" y2="14" stroke="url(#goldGradArr)" strokeWidth="2.2" />
                      
                      {/* Outer/Left Gold diagonal line */}
                      <line x1="66" y1="44" x2="108" y2="14" stroke="url(#goldGradArr)" strokeWidth="1.5" />
                    </svg>
                  </div>
                </div>

                {/* Form scroll area */}
                <div className="flex flex-col gap-2 p-3 overflow-y-auto flex-1">

                  {/* Target Audience */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 flex flex-col gap-1.5">
                    <label className="text-[10.5px] font-bold text-slate-600 uppercase tracking-wider" style={INTER}>Target Audience</label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => handleArrivalChange("TargetCustomerValue", "")}
                        className={`flex-1 h-8 rounded-md text-[11px] font-bold border flex items-center justify-center gap-1.5 cursor-pointer transition-all ${isArrivalBroadcast ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                        <FiUsers className="w-3.5 h-3.5" /> All Customers
                      </button>
                      <button type="button" onClick={() => { if (isArrivalBroadcast) handleArrivalChange("TargetCustomerValue", "_pick"); }}
                        className={`flex-1 h-8 rounded-md text-[11px] font-bold border flex items-center justify-center gap-1.5 cursor-pointer transition-all ${!isArrivalBroadcast ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                        <FiUser className="w-3.5 h-3.5" /> Specific Customer
                      </button>
                    </div>
                    {!isArrivalBroadcast && (
                      <select value={arrivalForm.TargetCustomerValue} onChange={e => handleArrivalChange("TargetCustomerValue", e.target.value)}
                        className="w-full h-[28px] px-2 border border-slate-200 rounded-md text-slate-800 text-[11px] outline-none hover:border-slate-300 focus:border-emerald-600 bg-white" style={INTER}>
                        <option value="_pick">— Select customer —</option>
                        {customerOptions.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    )}
                  </div>

                  {/* Product Name */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiStar className="w-3 h-3 text-emerald-500" /> Product Name <span className="text-rose-500">*</span>
                    </label>
                    <input type="text" ref={arrivalTitleRef} value={arrivalForm.Title} onChange={e => handleArrivalChange("Title", e.target.value)}
                      onKeyDown={e => handleEnterKey(e, arrivalPurityRef)}
                      maxLength={150} placeholder="E.g. Bridal Kundan Necklace Set"
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-emerald-600 transition-all" style={INTER} />
                    <span className="text-[9px] text-slate-400">{arrivalForm.Title.length}/150 characters</span>
                  </div>

                  {/* Metal Type */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiTag className="w-3 h-3 text-emerald-500" /> Metal Type
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {METAL_TYPES.map(m => {
                        const active = arrivalForm.MetalType === m.value;
                        return (
                          <button key={m.value} type="button" onClick={() => {
                            handleArrivalChange("MetalType", m.value);
                            handleArrivalChange("Purity", PURITY_OPTIONS[m.value]?.[0] || "Standard");
                          }}
                            style={{ backgroundColor: active ? m.bg : undefined, borderColor: active ? m.border : undefined, color: active ? m.color : undefined }}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-semibold cursor-pointer transition-all border ${active ? "border-2" : "border-slate-200 text-slate-500 hover:border-slate-300 bg-white"}`}
                          >
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Purity */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600" style={INTER}>Purity / standard</label>
                    <select ref={arrivalPurityRef} value={arrivalForm.Purity} onChange={e => handleArrivalChange("Purity", e.target.value)}
                      onKeyDown={e => handleEnterKey(e, arrivalWeightRef)}
                      className="w-full h-[28px] px-2 border border-slate-200 rounded-md text-slate-800 text-[11px] outline-none hover:border-slate-300 focus:border-emerald-600 bg-white" style={INTER}>
                      {(PURITY_OPTIONS[arrivalForm.MetalType] || ["Standard"]).map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  {/* Weight */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiInfo className="w-3 h-3 text-emerald-500" /> Approx. Weight (grams) <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input type="number" min="0" step="0.001" ref={arrivalWeightRef} value={arrivalForm.Weight} onChange={e => handleArrivalChange("Weight", e.target.value)}
                      onKeyDown={e => {
                        const target = arrivalForm.PricingType === "fixed" ? arrivalFixedPriceRef : arrivalMakingChargeRef;
                        handleEnterKey(e, target);
                      }}
                      placeholder="E.g. 12.5"
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-emerald-600 transition-all" style={INTER} />
                  </div>

                  {/* Pricing Type */}
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] font-bold text-slate-600" style={INTER}>Pricing Model</label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => handleArrivalChange("PricingType", "dynamic")}
                        className={`flex-1 h-7 rounded-md text-[10.5px] font-bold border flex items-center justify-center gap-1 cursor-pointer transition-all ${arrivalForm.PricingType === "dynamic" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                        Dynamic (Gold Rate)
                      </button>
                      <button type="button" onClick={() => handleArrivalChange("PricingType", "fixed")}
                        className={`flex-1 h-7 rounded-md text-[10.5px] font-bold border flex items-center justify-center gap-1 cursor-pointer transition-all ${arrivalForm.PricingType === "fixed" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                        Fixed Price
                      </button>
                    </div>
                  </div>

                  {/* Fixed Price */}
                  {arrivalForm.PricingType === "fixed" && (
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600" style={INTER}>Fixed Product Price (₹)</label>
                      <input type="number" min="0" ref={arrivalFixedPriceRef} value={arrivalForm.FixedPrice} onChange={e => handleArrivalChange("FixedPrice", e.target.value)}
                        onKeyDown={e => handleEnterKey(e, arrivalMakingChargeRef)}
                        placeholder="E.g. 75000"
                        className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-emerald-600 transition-all" style={INTER} />
                    </div>
                  )}

                  {/* Making Charge & Type */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600" style={INTER}>Making Charge (₹)</label>
                      <input type="number" min="0" ref={arrivalMakingChargeRef} value={arrivalForm.MakingCharge} onChange={e => handleArrivalChange("MakingCharge", e.target.value)}
                        onKeyDown={e => handleEnterKey(e, arrivalMakingChargeTypeRef)}
                        placeholder="E.g. 350"
                        className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-emerald-600 transition-all" style={INTER} />
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <label className="text-[11px] font-bold text-slate-600" style={INTER}>Charge Type</label>
                      <select ref={arrivalMakingChargeTypeRef} value={arrivalForm.MakingChargeType} onChange={e => handleArrivalChange("MakingChargeType", e.target.value)}
                        onKeyDown={e => handleEnterKey(e, arrivalGstPercentRef)}
                        className="w-full h-[28px] px-2 border border-slate-200 rounded-md text-slate-800 text-[11px] outline-none hover:border-slate-300 focus:border-emerald-600 bg-white" style={INTER}>
                        <option value="per_gram">Per Gram</option>
                        <option value="percentage">% of Gold Value</option>
                        <option value="flat">Flat Amount</option>
                      </select>
                    </div>
                  </div>

                  {/* GST Percentage */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600" style={INTER}>GST Rate (%)</label>
                    <input type="number" min="0" step="0.5" ref={arrivalGstPercentRef} value={arrivalForm.GstPercent} onChange={e => handleArrivalChange("GstPercent", e.target.value)}
                      onKeyDown={e => handleEnterKey(e, arrivalStockStatusRef)}
                      placeholder="E.g. 3"
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-emerald-600 transition-all" style={INTER} />
                  </div>

                  {/* Stock Status */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600" style={INTER}>Stock Status</label>
                    <select ref={arrivalStockStatusRef} value={arrivalForm.StockStatus} onChange={e => handleArrivalChange("StockStatus", e.target.value)}
                      onKeyDown={e => {
                        const target = (arrivalForm.StockStatus !== "out_of_stock" && arrivalForm.BookingEnabled) ? arrivalBookingAmountRef : arrivalMessageRef;
                        handleEnterKey(e, target);
                      }}
                      className="w-full h-[28px] px-2 border border-slate-200 rounded-md text-slate-800 text-[11px] outline-none hover:border-slate-300 focus:border-emerald-600 bg-white" style={INTER}>
                      <option value="in_stock">In Stock</option>
                      <option value="pre_order">Pre-Order</option>
                      <option value="out_of_stock">Out of Stock</option>
                    </select>
                  </div>

                  {/* Booking enabled */}
                  {arrivalForm.StockStatus !== "out_of_stock" && (
                    <>
                      <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-200/60 select-none">
                        <div className="flex flex-col">
                          <span className="text-[11px] font-bold text-slate-700" style={INTER}>Enable Online Booking Inquiry</span>
                          <span className="text-[9px] text-slate-400">Shows "Book Now" CTA with booking deposit</span>
                        </div>
                        <button type="button" onClick={() => handleArrivalChange("BookingEnabled", !arrivalForm.BookingEnabled)}
                          className={`w-10 h-[22px] flex items-center rounded-full p-0.5 cursor-pointer transition-colors duration-300 outline-none border-none ${arrivalForm.BookingEnabled ? "bg-emerald-500" : "bg-slate-300"}`}>
                          <div className={`bg-white w-[18px] h-[18px] rounded-full shadow-md transform transition-transform duration-300 ${arrivalForm.BookingEnabled ? "translate-x-[18px]" : "translate-x-0"}`} />
                        </button>
                      </div>

                      {/* Booking Amount */}
                      {arrivalForm.BookingEnabled && (
                        <div className="flex flex-col gap-0.5">
                          <label className="text-[11px] font-bold text-slate-600" style={INTER}>Booking Deposit (₹)</label>
                          <input type="number" min="0" ref={arrivalBookingAmountRef} value={arrivalForm.BookingAmount} onChange={e => handleArrivalChange("BookingAmount", e.target.value)}
                            onKeyDown={e => handleEnterKey(e, arrivalMessageRef)}
                            placeholder="E.g. 5000"
                            className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-emerald-600 transition-all" style={INTER} />
                        </div>
                      )}
                    </>
                  )}

                  {/* Description */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiMessageSquare className="w-3 h-3 text-emerald-500" /> Description
                    </label>
                    <textarea ref={arrivalMessageRef} value={arrivalForm.Message} onChange={e => handleArrivalChange("Message", e.target.value)}
                      onKeyDown={e => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          handleEnterKey(e, arrivalImageUrlRef);
                        }
                      }}
                      maxLength={800} rows={3} placeholder="Brief description of the item..."
                      className="w-full px-2.5 py-1.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-emerald-600 transition-all resize-none" style={INTER} />
                    <span className="text-[9px] text-slate-400">{arrivalForm.Message.length}/800 characters</span>
                  </div>

                  {/* Multi-Image Manager */}
                  <ImageManager
                    images={arrivalImages}
                    newUrlInput={newImageUrl}
                    onNewUrlChange={setNewImageUrl}
                    onAddImage={handleAddImage}
                    onDeleteImage={handleDeleteImage}
                    onMoveUp={(idx) => handleMoveImage(idx, "up")}
                    onMoveDown={(idx) => handleMoveImage(idx, "down")}
                    isSaving={arrivalSending}
                    inputRef={arrivalImageUrlRef}
                    onKeyDown={e => handleEnterKey(e, arrivalTitleRef)}
                  />

                  {/* Priority */}
                  <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-200/60 select-none">
                    <div className="flex flex-col">
                      <span className="text-[11px] font-bold text-slate-700 flex items-center gap-1" style={INTER}>
                        <FiZap className="w-3 h-3 text-amber-500" /> High Priority
                      </span>
                      <span className="text-[9px] text-slate-400">Featured at top of Arrivals section</span>
                    </div>
                    <button type="button" onClick={() => handleArrivalChange("Priority", arrivalForm.Priority === 1 ? 0 : 1)}
                      className={`w-10 h-[22px] flex items-center rounded-full p-0.5 cursor-pointer transition-colors duration-300 outline-none border-none ${arrivalForm.Priority === 1 ? "bg-[#b78a39]" : "bg-slate-300"}`}>
                      <div className={`bg-white w-[18px] h-[18px] rounded-full shadow-md transform transition-transform duration-300 ${arrivalForm.Priority === 1 ? "translate-x-[18px]" : "translate-x-0"}`} />
                    </button>
                  </div>

                  {/* Active */}
                  <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-200/60 select-none">
                    <div className="flex flex-col">
                      <span className="text-[11px] font-bold text-slate-700" style={INTER}>Active / Visible</span>
                      <span className="text-[9px] text-slate-400">Hidden arrivals won't appear in the app</span>
                    </div>
                    <button type="button" onClick={() => handleArrivalChange("IsActive", !arrivalForm.IsActive)}
                      className={`w-10 h-[22px] flex items-center rounded-full p-0.5 cursor-pointer transition-colors duration-300 outline-none border-none ${arrivalForm.IsActive ? "bg-emerald-500" : "bg-slate-300"}`}>
                      <div className={`bg-white w-[18px] h-[18px] rounded-full shadow-md transform transition-transform duration-300 ${arrivalForm.IsActive ? "translate-x-[18px]" : "translate-x-0"}`} />
                    </button>
                  </div>

                  {/* Expiry */}
                  <div className="flex flex-col gap-0.5">
                    <label className="text-[11px] font-bold text-slate-600 flex items-center gap-1" style={INTER}>
                      <FiCalendar className="w-3 h-3 text-emerald-500" /> Available Until <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input type="date" value={arrivalForm.ExpiresAt} onChange={e => handleArrivalChange("ExpiresAt", e.target.value)}
                      className="w-full h-[28px] px-2.5 border border-slate-200 rounded-md text-slate-800 text-[11.5px] outline-none hover:border-slate-300 focus:border-emerald-600 transition-all" style={INTER} />
                    <span className="text-[9px] text-slate-400">Leave empty — arrival stays visible indefinitely</span>
                  </div>

                  {/* Live Preview Card */}
                  <div className="mt-1">
                    <div className="flex items-center gap-1 mb-1.5">
                      <FiEye className="w-3 h-3 text-slate-400" />
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" style={INTER}>App Preview</span>
                    </div>
                    <ArrivalPreviewCard
                      title={arrivalForm.Title}
                      metalType={arrivalForm.MetalType}
                      weight={arrivalForm.Weight}
                      description={arrivalForm.Message}
                      imageUrl={arrivalImages[0]?.ImageUrl || ""}
                      isActive={arrivalForm.IsActive}
                      purity={arrivalForm.Purity}
                      pricingType={arrivalForm.PricingType}
                      fixedPrice={arrivalForm.FixedPrice}
                      makingCharge={arrivalForm.MakingCharge}
                      makingChargeType={arrivalForm.MakingChargeType}
                      gstPercent={arrivalForm.GstPercent}
                      stockStatus={arrivalForm.StockStatus}
                      bookingEnabled={arrivalForm.BookingEnabled}
                      bookingAmount={arrivalForm.BookingAmount}
                      goldRate={goldRate}
                    />
                  </div>
                </div>

                {/* Action buttons */}
                <div className="p-3 border-t border-slate-100 shrink-0 flex flex-col gap-2">
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={clearArrivalForm}
                      className="h-8 border border-slate-300 hover:bg-slate-50 text-slate-600 font-bold rounded-md transition-colors cursor-pointer text-xs" style={INTER}>
                      Clear Form
                    </button>
                    <button type="button" onClick={handleAddToQueue} disabled={arrivalEditing || !hasSavePermission}
                      className={`h-8 font-bold rounded-md transition-colors shadow-sm flex items-center justify-center gap-1.5 text-xs ${
                        arrivalEditing || !hasSavePermission
                          ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                          : "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                      }`}
                      title={!hasSavePermission ? "Permission Denied: Save permission required" : ""}
                      style={INTER}>
                      <FiPlus className="w-3.5 h-3.5" /> Queue Item
                    </button>
                  </div>
                  <button type="button" onClick={handleArrivalSave} disabled={arrivalSending || (arrivalEditing ? !hasEditPermission : !hasSavePermission)}
                    className={`h-8 font-bold rounded-md transition-colors shadow-sm flex items-center justify-center gap-1.5 text-xs ${
                      (arrivalEditing ? !hasEditPermission : !hasSavePermission)
                        ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                        : "bg-emerald-700 hover:bg-emerald-800 text-white cursor-pointer"
                    }`}
                    title={(arrivalEditing ? !hasEditPermission : !hasSavePermission) ? "Permission Denied" : ""}
                    style={INTER}>
                    <FiPackage className="w-3.5 h-3.5" />
                    {arrivalSending ? "Posting..." : arrivalEditing ? "Update Arrival" : "Post Direct"}
                  </button>
                  <div className="p-2 bg-emerald-50/70 border border-emerald-200/60 rounded-md flex items-center gap-2 text-emerald-800">
                    <FiInfo className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <p className="text-[10px] font-medium leading-tight" style={INTER}>
                      {isArrivalBroadcast
                        ? "This arrival card will appear in the Arrivals tab for ALL customers."
                        : "This arrival will be shown only to the selected customer."}
                    </p>
                  </div>
                </div>
              </div>

              {/* RIGHT: Arrivals Grid */}
              <div className="flex-1 bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col overflow-hidden">

                {/* Draft Queue Staging Area */}
                {draftArrivals.length > 0 && (
                  <div className="p-3 bg-blue-50/70 border-b border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="flex h-2 w-2 relative">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                        </span>
                        <h4 className="text-xs font-bold text-blue-900" style={INTER}>
                          Bulk Draft Queue ({draftArrivals.length} item{draftArrivals.length > 1 ? "s" : ""} staged)
                        </h4>
                      </div>
                      <button
                        type="button"
                        onClick={handlePostQueue}
                        disabled={arrivalSending || !hasSavePermission}
                        className={`px-3 py-1 text-white text-[10px] font-bold rounded flex items-center gap-1 border-none shadow-xs transition-colors ${
                          arrivalSending || !hasSavePermission
                            ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 cursor-pointer"
                        }`}
                        title={!hasSavePermission ? "Permission Denied: Save permission required" : ""}
                        style={INTER}
                      >
                        <FiSend className="w-3.5 h-3.5" /> Post All {draftArrivals.length} Staged Items
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto pr-1">
                      {draftArrivals.map((draft, idx) => (
                        <div key={draft._tempDraftId} className="flex items-center gap-2 px-2 py-1 bg-white rounded-lg border border-blue-200 text-[10.5px] shadow-2xs">
                          <span className="font-semibold text-slate-400">{idx + 1}.</span>
                          <span className="font-bold text-blue-950 truncate max-w-[120px]">{draft.form.Title}</span>
                          <span className="text-[9.5px] text-blue-600 bg-blue-50 px-1 rounded font-medium">
                            {draft.form.MetalType} ({draft.form.Purity})
                          </span>
                          <span className="text-[9.5px] text-slate-400">
                            {draft.images.length} img
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFromQueue(draft._tempDraftId)}
                            className="w-4 h-4 rounded-full flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 border-none bg-transparent cursor-pointer ml-1"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Grid header */}
                <div className="p-3.5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3 bg-white shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shadow-xs border border-emerald-100">
                      <FiPackage className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-slate-800 leading-none" style={INTER}>Posted Arrivals</h3>
                      <span className="text-[11.5px] text-slate-400 font-medium mt-1 block" style={INTER}>
                        All new arrivals visible in the SherGoldApp Arrivals tab
                      </span>
                    </div>
                  </div>
                  {/* Search */}
                  <div className="relative flex items-center">
                    <FiSearch className="absolute left-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    <input type="text" value={arrivalSearch} disabled={!hasFindPermission} onChange={e => setArrivalSearch(e.target.value)} placeholder={!hasFindPermission ? "Find permission disabled..." : "Search arrivals..."}
                      className={`h-8 pl-8 pr-3 border rounded-lg text-[11.5px] outline-none w-48 transition-all ${
                        !hasFindPermission ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed" : "border-slate-200 text-slate-700 hover:border-slate-300 focus:border-emerald-600"
                      }`} style={INTER} />
                  </div>
                </div>

                {/* Grid table */}
                <div className="flex-1 overflow-auto">
                  <table className="w-full text-[11.5px] border-collapse">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-slate-50 border-b border-slate-200">
                        {["Image", "Product & Details", "Metal", "Target", "Priority", "Status", "Available Until", "Posted On", "Actions"].map(h => (
                          <th key={h} className="px-3 py-2.5 text-left font-bold text-slate-500 uppercase tracking-wider text-[10px] whitespace-nowrap" style={INTER}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredArrivals.length === 0 ? (
                        <tr><td colSpan={9} className="text-center py-16 text-slate-400">
                          <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mx-auto mb-3">
                            <FiPackage className="w-7 h-7 text-emerald-300" />
                          </div>
                          <p className="font-semibold text-slate-400" style={INTER}>No arrivals posted yet</p>
                          <p className="text-[11px] text-slate-300 mt-1" style={INTER}>Post your first new arrival using the form on the left.</p>
                        </td></tr>
                      ) : (
                        filteredArrivals.map(item => {
                          const parsed = parseArrivalMessage(item.Message);
                          const metalCfg = METAL_TYPES.find(m => m.value === parsed.metalType) || METAL_TYPES[4];
                          return (
                            <tr key={item.Auto} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">

                              {/* Image thumbnail */}
                              <td className="px-3 py-2">
                                <div className="w-10 h-10 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                                  {item.ImageUrl
                                    ? <img src={item.ImageUrl} alt="product" className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
                                    : <FiImage className="w-4 h-4 text-slate-300" />}
                                </div>
                              </td>

                              {/* Product & Details */}
                              <td className="px-3 py-2 max-w-[200px]">
                                <p className="font-bold text-slate-800 truncate" style={INTER}>{item.Title}</p>
                                <p className="text-slate-400 truncate text-[10.5px] mt-0.5" style={INTER}>{parsed.description || "—"}</p>
                              </td>

                              {/* Metal */}
                              <td className="px-3 py-2 whitespace-nowrap">
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full w-fit"
                                    style={{ backgroundColor: metalCfg.bg, color: metalCfg.color, border: `1px solid ${metalCfg.border}` }}>
                                    {parsed.metalType}
                                  </span>
                                  {parsed.weight && (
                                    <span className="text-[9.5px] text-slate-400 font-medium" style={INTER}>~{parsed.weight}g</span>
                                  )}
                                </div>
                              </td>

                              {/* Target */}
                              <td className="px-3 py-2 whitespace-nowrap">
                                {item.TargetCustomerValue
                                  ? <span className="flex items-center gap-1 text-purple-700 font-semibold" style={INTER}><FiUser className="w-3 h-3" />Personal</span>
                                  : <span className="flex items-center gap-1 text-blue-600 font-semibold" style={INTER}><FiUsers className="w-3 h-3" />Broadcast</span>}
                              </td>

                              {/* Priority */}
                              <td className="px-3 py-2">
                                {item.Priority === 1
                                  ? <span className="flex items-center gap-1 text-amber-600 font-bold text-[10px]"><FiZap className="w-3 h-3" />Featured</span>
                                  : <span className="text-slate-400 text-[10px]">Normal</span>}
                              </td>

                              {/* Status */}
                              <td className="px-3 py-2">
                                <button onClick={() => handleToggle(item.Auto)} disabled={!hasEditPermission} className={`flex items-center gap-1 border-none bg-transparent p-0 ${!hasEditPermission ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`} title={!hasEditPermission ? "Permission Denied" : "Click to toggle"}>
                                  {item.IsActive
                                    ? <><FiToggleRight className="w-4 h-4 text-emerald-500" /><span className="text-emerald-600 font-semibold text-[10px]" style={INTER}>Active</span></>
                                    : <><FiToggleLeft className="w-4 h-4 text-slate-400" /><span className="text-slate-400 font-semibold text-[10px]" style={INTER}>Inactive</span></>}
                                </button>
                              </td>

                              {/* Expiry */}
                              <td className="px-3 py-2 whitespace-nowrap text-slate-500" style={INTER}>
                                {item.ExpiresAt ? fmtDate(item.ExpiresAt) : <span className="text-slate-300 text-[10px]">No limit</span>}
                              </td>

                              {/* Created */}
                              <td className="px-3 py-2 whitespace-nowrap text-slate-500" style={INTER}>{fmtDate(item.CreatedDate)}</td>

                              {/* Actions */}
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-1.5">
                                  <button onClick={() => { handleArrivalEdit(item); }} disabled={!hasEditPermission} className={`w-7 h-7 rounded-md flex items-center justify-center border transition-colors ${!hasEditPermission ? "bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed" : "bg-slate-100 hover:bg-blue-50 text-slate-500 hover:text-blue-600 border-slate-200 hover:border-blue-200 cursor-pointer"}`} title={!hasEditPermission ? "Permission Denied: Edit action disabled" : "Edit"}>
                                    <FiEdit3 className="w-3.5 h-3.5" />
                                  </button>
                                  <button onClick={() => handleDelete(item.Auto)} disabled={!hasDeletePermission} className={`w-7 h-7 rounded-md flex items-center justify-center border transition-colors ${!hasDeletePermission ? "bg-slate-100 text-slate-300 border-slate-200 cursor-not-allowed" : "bg-slate-100 hover:bg-red-50 text-slate-500 hover:text-red-600 border-slate-200 hover:border-red-200 cursor-pointer"}`} title={!hasDeletePermission ? "Permission Denied: Delete action disabled" : "Delete"}>
                                    <FiTrash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Grid footer */}
                <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 shrink-0 flex items-center justify-between">
                  <span className="text-[11px] text-slate-400 font-medium" style={INTER}>
                    Showing <strong>{filteredArrivals.length}</strong> of <strong>{arrivalCount}</strong> arrivals
                  </span>
                  <span className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1" style={INTER}>
                    <FiPackage className="w-3 h-3" />
                    {arrivals.filter(a => a.IsActive).length} active · {arrivals.filter(a => !a.IsActive).length} hidden
                  </span>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

export default AppBroadcastManager;
